import { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../../../Context/AuthContext";
import { Authorization } from "../../../Context/Authorization";
import Preloader from "../../../shared/PreLoaders";
import { logActivity } from "../../../utils/LogActivity";
import { toast } from "react-toastify";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s", cursor: "pointer",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };

const EditLoan = () => {
  const { loanId }    = useParams();
  const { userId }    = useContext(AuthContext);
  const Api           = process.env.REACT_APP_API;
  const navigate      = useNavigate();
  const [loanData, setLoanData]         = useState(null);
  const [members, setMembers]           = useState([]);
  const [installments, setInstallments] = useState([]);
  const [loanTypes, setLoanTypes]       = useState([]);
  const [fields, setFields]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [submitMessage, setSubmitMessage] = useState("");
  const [formData, setFormData]         = useState({ loanType: "", installment: "", cashOrBank: "", date: "" });

  useEffect(() => {
    const fetchInitialData = async () => {
      const config = Authorization();
      try {
        const [instRes, typeRes, loanRes] = await Promise.all([
          axios.get(`${Api}/api/installments`, config),
          axios.get(`${Api}/api/loan-types`, config),
          axios.get(`${Api}/api/loans/${loanId}`, config),
        ]);
        setInstallments(instRes.data);
        setLoanTypes(typeRes.data.loanTypes);
        const loan = loanRes.data.loan;
        setLoanData(loan);
        setFormData({
          loanType: loan.loanType?._id || "",
          installment: loan.installment?._id || "",
          cashOrBank: loan.cashOrBank,
          date: loan.date?.split("T")[0] || "",
          uploadDocument: null,
        });
        setFields(
          loan.subLoans.map((subLoan) => ({
            member: subLoan.memberId,
            subLoanId: subLoan.subLoanId,
            loanAmount: subLoan.disbursedAmount,
            purpose: subLoan.purpose || "",
          }))
        );
      } catch (error) {
        console.error("Error fetching edit data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [Api, loanId]);

  const handleFieldChange = (index, e) => {
    const values = [...fields];
    values[index][e.target.name] = e.target.value;
    setFields(values);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const confirmUpdate = window.confirm("⚠️ Are you sure you want to update this loan? This will overwrite the existing data.");
    if (!confirmUpdate) return;

    const config = Authorization();
    const payload = {
      loanType: formData.loanType,
      installment: formData.installment,
      cashOrBank: formData.cashOrBank,
      date: formData.date,
      amount: fields.reduce((sum, f) => sum + parseFloat(f.loanAmount || 0), 0),
      subLoans: fields.map((f) => ({
        memberId: typeof f.member === "object" ? f.member._id : f.member,
        disbursedAmount: Number(f.loanAmount),
        purpose: f.purpose || "",
        subLoanId: f.subLoanId,
      })),
    };

    try {
      const res = await axios.put(`${Api}/api/loans/${loanId}`, payload, config);
      if (res.status === 200) {
        toast.success(res.message || "Loan updated successfully!");
        setSubmitMessage("Loan updated successfully.");
        await logActivity(userId, "Loan Edited", "Edit Loan Page", Api);
        navigate("/loan/manage");
      } else {
        console.error("Update failed:", res);
        setSubmitMessage("Failed to update loan.");
      }
    } catch (error) {
      console.error("Error during update:", error);
      setSubmitMessage("Error while updating the loan.");
    }
  };

  if (loading) return <Preloader loading={true} />;

  return (
    <div>
      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Edit Loan</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>Loan</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Edit Loan</span>
        </div>
      </div>

      {submitMessage && (
        <div style={{ marginBottom: 16, padding: "10px 16px", background: "#E8F5E9", border: "1px solid #C8E6C9", borderRadius: 8, fontSize: 13, color: "#1B5E20" }}>
          <i className="mdi mdi-check-circle-outline" style={{ marginRight: 8 }} />{submitMessage}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* ── Loan Details Card ── */}
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
            <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
              <i className="mdi mdi-pencil" />
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Loan Details</span>
          </div>
          <div style={{ padding: "20px 24px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
              <div>
                <label style={labelStyle}>Loan Type</label>
                <select value={formData.loanType} onChange={(e) => setFormData({ ...formData, loanType: e.target.value })} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                  <option value="">Select Loan Type</option>
                  {loanTypes.map((lt) => (
                    <option key={lt._id} value={lt._id}>{lt.loanName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Installment</label>
                <select value={formData.installment} onChange={(e) => setFormData({ ...formData, installment: e.target.value })} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                  <option value="">Select Installment</option>
                  {installments.map((i) => (
                    <option key={i._id} value={i._id}>{i.installmentNumber}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Cash or Bank</label>
                <select value={formData.cashOrBank} onChange={(e) => setFormData({ ...formData, cashOrBank: e.target.value })} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                  <option value="">Select</option>
                  <option value="cash">Cash</option>
                  <option value="bank">Bank</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Date</label>
                <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
              </div>
            </div>
          </div>
        </div>

        {/* ── Sub Loans Table ── */}
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
            <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
              <i className="mdi mdi-account-multiple-outline" />
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", flex: 1 }}>Sub Loans</span>
            <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 50 }}>{fields.length} entries</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {["#", "Member Name", "SubLoan No", "Loan Amount"].map((h) => (
                    <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontWeight: 600, fontSize: 12, color: "#4A5568", borderBottom: "1px solid #EDF2F7", background: "#F7FAFC", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fields.map((field, index) => (
                  <tr key={index} style={{ borderBottom: "1px solid #EDF2F7" }}>
                    <td style={{ padding: "10px 14px", color: "#718096" }}>{index + 1}</td>
                    <td style={{ padding: "10px 14px", color: "#1A202C" }}>
                      <input
                        value={typeof field.member === "object" ? field.member.name : members.find((m) => m._id === field.member)?.name || "N/A"}
                        disabled
                        style={{ ...inputStyle, background: "#F7FAFC", cursor: "default", width: "100%" }}
                      />
                    </td>
                    <td style={{ padding: "10px 14px", color: "#4A5568" }}>
                      <input value={field.subLoanId || "N/A"} disabled style={{ ...inputStyle, background: "#F7FAFC", cursor: "default", width: "100%" }} />
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <input type="number" name="loanAmount" value={field.loanAmount} onChange={(e) => handleFieldChange(index, e)} min={0}
                        style={{ ...inputStyle, width: "100%" }}
                        onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <button type="submit"
          style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 24px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)" }}>
          <i className="mdi mdi-content-save" /> Update Loan
        </button>
      </form>
    </div>
  );
};

export default EditLoan;
